/**
 * The app's built-in textures, resolved through the catalog.
 *
 * A project names a wall's finish by the app's own path (`wall/bricks/concrete/42.jpg`), and
 * a model's definition names a slot's finish by a short name (`wood-white`) that the app looks
 * up among its recipe files under `Textures/furniture`. Neither is a file the web has. The
 * catalog publishes each texture as a model whose `builtinId` is that path, and whose
 * `definition` says which uploaded picture, tint, surface type and shininess it stands for
 * (`docs/client-export-materials-and-models.md`).
 *
 * Every reference asked for in one turn goes to the catalog in one request; answers are kept
 * for the session and announced when they arrive, so a scene built before them picks them up.
 * A texture the catalog has not published yet answers `null`, and the caller keeps whatever
 * it did before.
 */
import { fetchBuiltinDocuments, mongoEscape, type CatalogModel } from './catalogApi';
import { definitionSurface, parseDefinition } from './materials';
import type { Surface } from '../core/hp/types';

const TEXTURES = 'Assets/Resources/Textures/';
/** The subfolders of the app's `Textures/furniture`, where the short-named recipes live (September 2026). */
const FURNITURE_FOLDERS = ['bedclothes', 'ceramic', 'colors', 'curtains', 'electronics', 'fabric', 'fire', 'glass', 'ground', 'leather',
  'metal', 'moldings', 'neon', 'other', 'paint', 'paper', 'pillow', 'plants', 'plastic', 'rubber', 'slats', 'stone', 'wickerwork', 'wood'];
const IMAGE = /\.(jpe?g|png)$/i;

/** The texture path without the app's prefixes, or nothing for what is not a library texture. */
function libraryPath(ref: string): string | undefined {
  const path = ref.replace(/\\/g, '/').replace(/^Assets\/Resources\/Textures\//, '').replace(/^Textures\//, '').trim();
  if (!path || path === 'mirror' || path.startsWith('[custom]') || path.split('/').some(part => !part || part === '..')) return undefined;
  // A bare picture name is a file out of the model's own archive, embedded by the converter.
  if (!path.includes('/') && IMAGE.test(path)) return undefined;
  return path;
}

/** The catalog keys a texture reference may be published under, the most specific first. */
export function builtinCandidates(ref: string): string[] {
  const path = libraryPath(ref);
  if (!path) return [];
  const stem = path.replace(/\.[^./]+$/, '');
  if (path.includes('/')) {
    // A picture's own document, then the recipe named like it; a recipe file is the recipe.
    const keys = path.endsWith('.xml') ? [stem] : [path, stem];
    return keys.map(key => TEXTURES + mongoEscape(key));
  }
  return FURNITURE_FOLDERS.map(folder => TEXTURES + mongoEscape(`furniture/${folder}/${stem}`));
}

type Answer = Surface | null;
const answers = new Map<string, Answer>();
const pending = new Map<string, { promise: Promise<Answer>; settle: (answer: Answer) => void }>();
const definitions = new Map<string, Promise<Surface | null>>();
const GATHER_MS = 100;
let queue: string[] = [];
let scheduled = false;
let version = 0;
const listeners = new Set<() => void>();

/** Grows every time new surfaces arrive; a scene built under an older one has textures to pick up. */
export function builtinTexturesVersion(): number { return version; }

export function subscribeBuiltinTextures(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/**
 * What the catalog publishes for a texture reference, when that is already known: the
 * surface, `null` for a texture it does not have, `undefined` while the answer is on its way
 * (asking for it, when nobody has yet).
 */
export function knownBuiltinSurface(ref: string): Surface | null | undefined {
  const known = answers.get(ref);
  if (known !== undefined) return known;
  if (libraryPath(ref)) void ask(ref);
  return undefined;
}

/** The published surfaces of several references at once, `null` where the catalog has none. */
export async function builtinSurfaces(refs: string[]): Promise<Map<string, Surface | null>> {
  const out = new Map<string, Surface | null>();
  await Promise.all([...new Set(refs)].map(async ref => {
    const known = answers.get(ref);
    out.set(ref, known !== undefined ? known : libraryPath(ref) ? await ask(ref) : null);
  }));
  return out;
}

function ask(ref: string): Promise<Answer> {
  const waiting = pending.get(ref);
  if (waiting) return waiting.promise;
  let settle!: (answer: Answer) => void;
  const promise = new Promise<Answer>(resolve => { settle = resolve; });
  pending.set(ref, { promise, settle });
  queue.push(ref);
  if (!scheduled) {
    // Models of a project arrive one after another over a few seconds: a short window lets
    // the slots of several of them share one request.
    scheduled = true;
    setTimeout(() => { scheduled = false; void flush(queue.splice(0)); }, GATHER_MS);
  }
  return promise;
}

/** One catalog request for everything asked in the turn; a failed request is not remembered. */
async function flush(refs: string[]): Promise<void> {
  const candidates = new Map(refs.map(ref => [ref, builtinCandidates(ref)]));
  let documents: Record<string, CatalogModel> = {};
  try {
    documents = await fetchBuiltinDocuments([...candidates.values()].flat());
  } catch {
    for (const ref of refs) { pending.get(ref)?.settle(null); pending.delete(ref); }
    return;
  }
  let arrived = false;
  await Promise.all(refs.map(async ref => {
    const key = candidates.get(ref)?.find(candidate => documents[candidate]);
    const document = key ? documents[key] : undefined;
    const answer = document ? await publishedSurface(document) : null;
    // A network failure while reading the definition answers nothing and is asked again later.
    // Only a surface is worth telling a built scene about: `null` leaves it as it is.
    if (answer !== undefined) { answers.set(ref, answer); if (answer) arrived = true; }
    pending.get(ref)?.settle(answer ?? null);
    pending.delete(ref);
  }));
  if (arrived) {
    version++;
    for (const listener of listeners) listener();
  }
}

/** The surface a texture's document stands for: `null` without a usable definition, `undefined` when it could not be read. */
function publishedSurface(document: CatalogModel): Promise<Surface | null | undefined> {
  const url = typeof document.definition === 'string' && /^https?:\/\//.test(document.definition) ? document.definition : undefined;
  if (!url) return Promise.resolve(null);
  let reading = definitions.get(url);
  if (!reading) {
    const width = document.width && document.width > 0 ? document.width : undefined;
    const height = document.height && document.height > 0 ? document.height : width;
    const fallback = { tile: width ? [Math.max(width, 5) / 100, Math.max(height ?? width, 5) / 100] as [number, number] : undefined, caption: document.title?.trim() };
    reading = fetch(url).then(async response => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return definitionSurface(parseDefinition(await response.text()), fallback) ?? null;
    });
    definitions.set(url, reading);
    reading.catch(() => { if (definitions.get(url) === reading) definitions.delete(url); });
  }
  return reading.catch(() => undefined);
}

/** Forgets every answer: for tests. */
export function resetBuiltinTextures(): void {
  answers.clear(); pending.clear(); definitions.clear(); queue = []; scheduled = false; version = 0;
}
